import { HttpClient } from '@angular/common/http';
import { Component, ChangeDetectionStrategy, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthenticationErrorStatus, AuthenticationRoleResponse, AuthenticationRoleSessionItem } from '@jms/model';
import { ApplicationActions, ApplicationState } from '@jms/tenant/module.jms.state';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { UtilService } from '@jms/utils/util.services';
import { Store } from '@ngrx/store';
import { SessionItem } from '@workbuddy/ui/v2/components';
import { SessionLimitBlockComponent } from '@workbuddy/ui/v2/blocks/session.limit';

@Component({
    selector: 'authorise',
    imports: [SessionLimitBlockComponent],
    template: `
    @if ($blockedLogin(); as blockedLogin) {
        <wb-session-limit-block [sessions]="$sessions()" [limit]="blockedLogin.limit ?? 2" (endSession)="terminateSession($event)" (endAll)="terminateAllSessions()" (tryAgain)="retryLogin()" />
    } @else if ($shouldShowSplash()) {
        <div class="splash">
            <div class="splash-text">
                <span class="jms"><img src="/images/WorkBuddy.svg" /></span>
            </div>
        </div>
    }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthoriseComponent {

    private readonly actions = inject(ApplicationActions);
    private readonly destroyRef = inject(DestroyRef);
    private readonly http = inject(HttpClient);
    private readonly store = inject(Store<{ application: ApplicationState }>);
    private readonly isLoading = signal(true);

    public readonly $login = toSignal(this.store.select(x => x.application.login), { initialValue: null });
    public readonly $blockedLogin = computed(() => {
        const login = this.$login();
        return login?.errorStatus === AuthenticationErrorStatus.ConcurrentSessionLimitReached ? login : null;
    });
    public readonly $shouldShowSplash = computed(() => this.isLoading() && !this.$blockedLogin());
    public readonly $sessions = computed(() => {
        const sessions = (this.$blockedLogin()?.sessions ?? []) as AuthenticationRoleSessionItem[];
        return sessions.map(session => ({ ...session, startedAt: new Date(session.startedAt) })) as SessionItem[];
    });

    constructor() {

        const domain = UtilService.getLocation().hostName;
        const token = UtilService.getURLParameter('token');
        let returnUrl = UtilService.getURLParameter('returnUrl');

        if (returnUrl) {
            returnUrl = decodeURIComponent(UtilService.getURLParameter('returnUrl'));
        }

        effect(() => {
            const login = this.$login();
            if (!login) {
                return;
            }

            this.isLoading.set(false);

            if (login && !login.success && login.errorStatus !== AuthenticationErrorStatus.ConcurrentSessionLimitReached) {
                this.redirectToLogin();
            }
        });

        if (token) {
            this.isLoading.set(true);
            this.actions.loginAuthorise(token, domain, returnUrl);
        } else {
            this.redirectToLogin();
        }
    }

    public terminateSession(session: AuthenticationRoleSessionItem): void {

        const token = UtilService.getURLParameter('token');
        if (!token || !session?.id) {
            return;
        }

        this.http.post<AuthenticationRoleResponse>('/api/authentication/role/session/terminate', { token, sessionId: session.id })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    if (response?.success) {
                        this.retryLogin();
                        return;
                    }

                    this.isLoading.set(false);
                    this.actions.loginError(response, true);
                },
                error: () => {
                    this.redirectToLogin();
                }
            });
    }

    public terminateAllSessions(): void {

        const token = UtilService.getURLParameter('token');
        if (!token) {
            return;
        }

        this.http.post<AuthenticationRoleResponse>('/api/authentication/role/session/terminate-all', { token })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    if (response?.success) {
                        this.retryLogin();
                        return;
                    }

                    this.isLoading.set(false);
                    this.actions.loginError(response, true);
                },
                error: () => {
                    this.redirectToLogin();
                }
            });
    }

    public retryLogin(): void {

        const domain = UtilService.getLocation().hostName;
        const token = UtilService.getURLParameter('token');
        let returnUrl = UtilService.getURLParameter('returnUrl');

        if (!token) {
            return;
        }

        if (returnUrl) {
            returnUrl = decodeURIComponent(UtilService.getURLParameter('returnUrl'));
        }

        this.isLoading.set(true);
        this.actions.loginAuthorise(token, domain, returnUrl, true);
    }

    private redirectToLogin(): void {

        const callbackUrl = `${window.location.origin}/callback`;
        // Carry the tenant's advanced SSO hint so the id app can pin login to their own Entra app.
        UtilService.navigate(ConfigService.environment.jms.logoutHref, callbackUrl, UtilService.ssoHintParams(ConfigService.environment.sso));
    }
}
